import { buildApiUrl, fetchJSON, validateRequiredParams } from '../crm/crmApi';

export type SupplierContactRole =
  | 'account_manager'
  | 'credit_control'
  | 'logistics'
  | 'executive'
  | 'operations'
  | 'other';

export interface SupplierContactInput {
  organization_id: string;
  supplier_id: string;
  full_name: string;
  job_title?: string;
  role: SupplierContactRole;
  email?: string;
  phone?: string;
  notes?: string;
  is_primary?: boolean;
}

export interface SupplierContact extends SupplierContactInput {
  id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ListSupplierContactsParams {
  organization_id: string;
  supplier_id?: string;
  role?: SupplierContactRole;
  search?: string;
  limit?: number;
  offset?: number;
}

const contactsService = {
  async list(params: ListSupplierContactsParams): Promise<{ data: SupplierContact[]; count: number | null }>{
    validateRequiredParams(params, ['organization_id'], {
      service: 'supplierCrm.contactsService.list'
    });
    const url = buildApiUrl('/api/supplier-crm/contacts', params as any);
    return fetchJSON(url, {
      description: 'List supplier contacts',
      context: {
        organization_id: params.organization_id,
        supplier_id: params.supplier_id,
        role: params.role
      }
    });
  },
  async get(id: string, organization_id: string): Promise<{ data: SupplierContact | null }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'supplierCrm.contactsService.get'
    });
    const url = buildApiUrl(`/api/supplier-crm/contacts/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      description: 'Get supplier contact',
      context: { id, organization_id }
    });
  },
  async create(payload: SupplierContactInput): Promise<{ data: SupplierContact }>{
    validateRequiredParams(payload, ['organization_id', 'supplier_id', 'full_name', 'role'], {
      service: 'supplierCrm.contactsService.create'
    });
    const url = buildApiUrl('/api/supplier-crm/contacts');
    return fetchJSON(url, {
      method: 'POST',
      body: payload,
      description: 'Create supplier contact',
      context: { organization_id: payload.organization_id, supplier_id: payload.supplier_id }
    });
  },
  async update(id: string, payload: Partial<SupplierContactInput> & { organization_id: string }): Promise<{ data: SupplierContact }>{
    validateRequiredParams({ id, ...payload }, ['id', 'organization_id'], {
      service: 'supplierCrm.contactsService.update'
    });
    const url = buildApiUrl(`/api/supplier-crm/contacts/${encodeURIComponent(id)}`);
    return fetchJSON(url, {
      method: 'PUT',
      body: { ...payload, id },
      description: 'Update supplier contact',
      context: { id, organization_id: payload.organization_id }
    });
  },
  async remove(id: string, organization_id: string): Promise<{ success: boolean }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'supplierCrm.contactsService.remove'
    });
    const url = buildApiUrl(`/api/supplier-crm/contacts/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      method: 'DELETE',
      description: 'Delete supplier contact',
      context: { id, organization_id }
    });
  }
};

export default contactsService;
